import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { IAgentUser, COMPANY_USER_ROLE, PROFILE_STAGE } from 'domain/entities/AgentUser/AgentUser';
import { IMatchForAgent, MATCH_STATUSES } from 'domain/entities/Match/Match';
import { useLoading } from 'application/contexts/useLoading';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentMatchQuery } from 'interfaceAdapter/queries/AgentMatch';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

type AgentUserContext = {
    /** ログイン中のユーザー */
    user: IAgentUser | null;
    setUser: (user: IAgentUser) => void;
    resetUser: () => void;

    /** ログイン後に遷移するURL */
    urlAfterLogin: string;
    /** ログイン後URLを設定 */
    setUrlAfterLogin: (url: string) => void;

    waitingFirstContactCount: number;
    needFirstContactMeetRoomIds: string[];
    existsUnreadMsg: boolean;
    isMeetingDateNotSet: boolean; // 面談日未設定のマッチングがあるか
    offerList: IMatchForAgent[]; // マッチング前のオファー
    matchList: IMatchForAgent[];
    meetingDateSetMatchList: IMatchForAgent[]; // 面談日が設定されているマッチング
    notCompletedProfile: boolean; // プロフィールが未完成
};

const defaultContext: AgentUserContext = {
    user: null,
    setUser: () => {},
    resetUser: () => {},
    urlAfterLogin: '/agent/dashboard/',
    setUrlAfterLogin: () => {},
    waitingFirstContactCount: 0,
    needFirstContactMeetRoomIds: [],
    existsUnreadMsg: false,
    isMeetingDateNotSet: false,
    offerList: [],
    matchList: [],
    meetingDateSetMatchList: [],
    notCompletedProfile: false,
};

export const agentUserContext = createContext<AgentUserContext>(defaultContext);
export const useAgentUser = (): AgentUserContext => {
    // TODO ビルドをサボっている
    // const [user, setUser] = useState<IAgentUser>(new AgentUserFactory().buildEmpty());
    const [user, setUser] = useState<IAgentUser | null>(null);
    const [urlAfterLogin, setUrlAfterLogin] = useState('/agent/dashboard/');
    const diContainer = useContext(diContainerContext);
    const agentMatchQuery = diContainer.resolve(AgentMatchQuery);
    const [matchList, setMatchList] = useState<IMatchForAgent[]>([]);
    const [meetingDateSetMatchList, setMeetingDateSetMatchList] = useState<IMatchForAgent[]>([]);
    const [offerList, setOfferList] = useState<IMatchForAgent[]>([]);
    const [isMeetingDateNotSet, setIsMeetingDateNotSet] = useState(false);
    const [existsUnreadMsg, setExistsUnreadMsg] = useState(false);
    const [notCompletedProfile, setNotCompletedProfile] = useState(false);
    const today = dayjs();
    const { setLoading } = useLoading();
    const navigate = useNavigate();

    const resetUser = useCallback(async () => {
        setUser(null);
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }
        if (user.role === COMPANY_USER_ROLE.USER && user.account_profile_stage === PROFILE_STAGE.NEED_USER_PROFILE) {
            setNotCompletedProfile(true);
            navigate('/agent/registration/user_profile/');
        }
        const fetchMatchList = async () => {
            setLoading(true);

            try {
                const matchList = await agentMatchQuery.fetchList();
                setMatchList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED));
                setOfferList(matchList.filter((match) => match.status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT));
                setMeetingDateSetMatchList(
                    matchList.filter(
                        (match) =>
                            match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED &&
                            match.meeting_date &&
                            dayjs(match.meeting_date).diff(today, 'day') >= 0,
                    ),
                );
                // マッチングから1日以上経過しているが、面談日が未設定
                setIsMeetingDateNotSet(
                    matchList.some(
                        (match) =>
                            !match.archived &&
                            match.status === MATCH_STATUSES.MATCH_STATUS_CONNECTED &&
                            !match.meeting_date &&
                            dayjs(match.date_matched).diff(today, 'day') <= 1,
                    ),
                );
                // メッセージ未読
                setExistsUnreadMsg(matchList.some((match) => match.chat_room && match.chat_room.unread));
            } finally {
                setLoading(false);
            }
        };
        fetchMatchList();
    }, [user]);

    return {
        user,
        setUser,
        resetUser,

        urlAfterLogin,
        setUrlAfterLogin,

        waitingFirstContactCount: 0,
        needFirstContactMeetRoomIds: [],
        existsUnreadMsg,
        isMeetingDateNotSet,
        offerList,
        matchList,
        meetingDateSetMatchList,
        notCompletedProfile,
    };
};
